import { useEffect } from 'react';
import { SITE_URL } from '../config/business';
import type { JsonLd } from '../utils/structuredData';

interface SeoMeta {
  title: string;
  description: string;
  /** Chemin de la page, ex. `/reparation-telephone-viry` (`/` pour l'accueil). */
  path: string;
  image?: string;
  /** Données structurées propres à la page (injectées puis retirées au démontage). */
  jsonLd?: JsonLd[];
}

const DEFAULT_IMAGE = `${SITE_URL}/images/website-hero.png`;
const PAGE_JSONLD_ATTR = 'data-page-jsonld';

const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

/**
 * Met à jour title, meta description, canonical, Open Graph, Twitter Card
 * et JSON-LD de page. Le script de prerender capture le résultat dans le HTML statique.
 */
export const useSeoMeta = ({ title, description, path, image = DEFAULT_IMAGE, jsonLd = [] }: SeoMeta) => {
  const jsonLdKey = JSON.stringify(jsonLd);

  useEffect(() => {
    const url = path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;

    document.title = title;
    upsertMeta('name', 'description', description);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    // Purge les JSON-LD de page déjà présents (HTML prérendu ou page précédente) pour éviter les doublons.
    document.head.querySelectorAll(`script[${PAGE_JSONLD_ATTR}]`).forEach((node) => node.remove());

    const scripts = (JSON.parse(jsonLdKey) as JsonLd[]).map((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(PAGE_JSONLD_ATTR, '');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      return script;
    });

    return () => scripts.forEach((script) => script.remove());
  }, [title, description, path, image, jsonLdKey]);
};
